import React from "react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Clock } from "lucide-react";

/**
 * Two-select time picker (HH:MM, 24h). Emits ISO-like "HH:MM" strings so it
 * is a drop-in replacement for <input type="time">.
 *
 * Props:
 *   - value: "HH:MM" string (defaults to "00:00")
 *   - onChange: (next: "HH:MM") => void
 *   - step: minute granularity for the minutes list (default 5)
 *   - disabled, testid
 */
export function TimePicker({ value = "00:00", onChange, step = 5, disabled, testid }) {
  const [h, m] = (value || "00:00").split(":");
  const hour = String(Math.max(0, Math.min(23, parseInt(h || "0", 10) || 0))).padStart(2, "0");
  const minute = String(Math.max(0, Math.min(59, parseInt(m || "0", 10) || 0))).padStart(2, "0");

  const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
  const minutes = Array.from({ length: Math.ceil(60 / step) }, (_, i) => String(i * step).padStart(2, "0"));

  const emit = (nh, nm) => {
    if (typeof onChange === "function") onChange(`${nh}:${nm}`);
  };

  return (
    <div
      className="inline-flex items-center gap-1.5 rounded-lg border border-[#EBEBEA] bg-white px-2 h-10"
      data-testid={testid}
    >
      <Clock className="w-3.5 h-3.5 text-[#5F7368]" />
      <Select value={hour} onValueChange={(v) => emit(v, minute)} disabled={disabled}>
        <SelectTrigger
          className="h-8 w-[62px] border-0 bg-transparent shadow-none focus:ring-0 px-1 text-sm font-medium tabular-nums"
          data-testid={testid ? `${testid}-hour` : undefined}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-64">
          {hours.map((hh) => (
            <SelectItem key={hh} value={hh} className="tabular-nums">{hh}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-sm text-[#8A9992]">:</span>
      <Select value={minute} onValueChange={(v) => emit(hour, v)} disabled={disabled}>
        <SelectTrigger
          className="h-8 w-[62px] border-0 bg-transparent shadow-none focus:ring-0 px-1 text-sm font-medium tabular-nums"
          data-testid={testid ? `${testid}-minute` : undefined}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-64">
          {minutes.map((mm) => (
            <SelectItem key={mm} value={mm} className="tabular-nums">{mm}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
